import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
} from 'vue';
import {
    SALEHOO_TOOL_EMBED_MESSAGE_TYPE,
    SALEHOO_TOOL_EMBED_ORIGIN_ATTRIBUTE,
    SALEHOO_TOOL_MAX_EMBED_HEIGHT,
} from './embedConfig';
import {
    getUrlOrigin,
    getValidatedEmbedHeight,
} from './embedMessaging';

export const TOOL_EMBED_PROPS = {
    embedUrl: {
        type: String,
        default: '',
    },
    embed: {
        type: Boolean,
        default: false,
    },
    preview: {
        type: Boolean,
        default: false,
    },
};

export function appendEmbedPath(url) {
    if (!url) {
        return '';
    }

    const [rawPath, suffix = ''] = url.split(/([?#].*)/, 2);
    const cleanPath = rawPath.replace(/\/embed:(?:1|true)\/?$/i, '');

    return `${cleanPath.replace(/\/$/, '')}/embed:1${suffix}`;
}

export function detectEmbedMode(url, search = '') {
    return /\/embed:(1|true)(?:$|[/?#])/i.test(url) || /[?&]embed=(1|true)\b/i.test(search);
}

export function buildEmbedSnippet({
    pageUrl,
    iframeTitle,
    initialHeight = 1500,
    maxWidth = 760,
}) {
    if (!pageUrl) {
        return '';
    }

    const embedUrl = appendEmbedPath(pageUrl);
    const embedOrigin = getUrlOrigin(embedUrl, pageUrl);
    const originAttribute = embedOrigin ? ` ${SALEHOO_TOOL_EMBED_ORIGIN_ATTRIBUTE}="${embedOrigin}"` : '';
    const inlineScript = `(function(s,f,o){if(!f)return;o=f.getAttribute(${JSON.stringify(SALEHOO_TOOL_EMBED_ORIGIN_ATTRIBUTE)});addEventListener("message",function(e){var d=e&&e.data,h=Math.ceil(Number(d&&d.height));if(!d||d.type!==${JSON.stringify(SALEHOO_TOOL_EMBED_MESSAGE_TYPE)}||e.source!==f.contentWindow||(o&&e.origin!==o)||!isFinite(h)||h<=0||h>${SALEHOO_TOOL_MAX_EMBED_HEIGHT})return;f.style.height=h+"px";});})(document.currentScript,document.currentScript&&document.currentScript.previousElementSibling&&document.currentScript.previousElementSibling.firstElementChild);`;

    return `<div style="max-width:${maxWidth}px;margin:0 auto">\n  <iframe src="${embedUrl}" title="${iframeTitle}" loading="lazy" referrerpolicy="strict-origin-when-cross-origin" sandbox="allow-scripts allow-same-origin" style="width:100%;height:${initialHeight}px;border:0;display:block"${originAttribute}></iframe>\n</div>\n<script>${inlineScript}<\/script>`;
}

export function useToolEmbedSupport({
    props,
    messageType = SALEHOO_TOOL_EMBED_MESSAGE_TYPE,
    previewHeight = 420,
}) {
    const inBrowser = typeof window !== 'undefined';
    const rootRef = ref(null);
    const previewFrameRef = ref(null);
    const showPreview = ref(false);

    const isEmbed = computed(() => (
        props.embed || (inBrowser && detectEmbedMode(window.location.href, window.location.search))
    ));
    const pageUrl = computed(() => (
        props.embedUrl || (inBrowser ? window.location.href.split(/[?#]/)[0] : '')
    ));
    const previewSrc = computed(() => appendEmbedPath(pageUrl.value));
    const showEmbedCard = computed(() => !isEmbed.value && !!pageUrl.value);

    let resizeObserver;
    let windowMessageHandler;
    let previewKeyHandler;
    let lastPostedHeight = 0;
    let parentMessageOrigin = '*';
    let previousBodyOverflow = '';

    function closePreview() {
        showPreview.value = false;
    }

    function postEmbedHeight() {
        if (!inBrowser || !isEmbed.value || props.preview || window.parent === window || !rootRef.value) {
            return;
        }

        const height = Math.ceil(rootRef.value.getBoundingClientRect().height);

        if (height && height !== lastPostedHeight) {
            lastPostedHeight = height;
            window.parent.postMessage({
                type: messageType,
                height,
            }, parentMessageOrigin);
        }
    }

    onMounted(() => {
        if (inBrowser && isEmbed.value && !props.preview && window.parent !== window) {
            parentMessageOrigin = getUrlOrigin(window.location.ancestorOrigins?.[0], window.location.href)
                || getUrlOrigin(document.referrer, window.location.href)
                || '*';

            nextTick(() => {
                postEmbedHeight();
            });

            if ('ResizeObserver' in window && rootRef.value) {
                resizeObserver = new ResizeObserver(() => {
                    postEmbedHeight();
                });
                resizeObserver.observe(rootRef.value);
            }

            window.addEventListener('resize', postEmbedHeight);
        }

        if (inBrowser) {
            windowMessageHandler = (event) => {
                const height = getValidatedEmbedHeight(event?.data?.height);
                const expectedOrigin = previewFrameRef.value
                    ? getUrlOrigin(previewFrameRef.value.getAttribute('src'), pageUrl.value)
                    : '';

                if (
                    event?.data?.type === messageType
                    && previewFrameRef.value
                    && previewFrameRef.value.contentWindow === event.source
                    && height
                    && (!expectedOrigin || event.origin === expectedOrigin)
                ) {
                    previewFrameRef.value.style.height = `${height}px`;
                }
            };

            window.addEventListener('message', windowMessageHandler);
        }
    });

    watch(showPreview, (isOpen) => {
        if (!inBrowser) {
            return;
        }

        if (isOpen) {
            previousBodyOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';

            previewKeyHandler = (event) => {
                if (event.key === 'Escape') {
                    showPreview.value = false;
                }
            };
            window.addEventListener('keydown', previewKeyHandler);

            nextTick(() => {
                if (previewFrameRef.value) {
                    previewFrameRef.value.style.height = `${previewHeight}px`;
                }
            });

            return;
        }

        if (previewKeyHandler) {
            window.removeEventListener('keydown', previewKeyHandler);
            previewKeyHandler = undefined;
        }

        document.body.style.overflow = previousBodyOverflow;
    });

    onBeforeUnmount(() => {
        if (previewKeyHandler) {
            window.removeEventListener('keydown', previewKeyHandler);
        }

        if (windowMessageHandler) {
            window.removeEventListener('message', windowMessageHandler);
        }

        if (resizeObserver) {
            resizeObserver.disconnect();
        }

        if (inBrowser) {
            window.removeEventListener('resize', postEmbedHeight);
            document.body.style.overflow = previousBodyOverflow;
        }
    });

    return {
        rootRef,
        previewFrameRef,
        showPreview,
        isEmbed,
        pageUrl,
        previewSrc,
        showEmbedCard,
        closePreview,
    };
}
